import { Fragment, useState } from 'react'
import { api } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { verweisJeBefund, type Sprungziel } from './Meldungen'
import { Periodenfeld } from './Periodenfeld'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { anzahlwort, beschriftung, bewertungsebenenname, gesellschaftsname, kreisname, periodenname, satz, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import type { Auswahl, Beschriftung, Textbaustein } from './typen'
import { Verweissatz } from './Verweissatz'
import { Zustandssymbol, type Zustandsart } from './Zustand'

/**
 * **Die Arbeitsliste überwacht, was der Anwender festlegt** (7.1 Abschnitt 12,
 * R1512–R1521, R1556–R1558).
 *
 * Der Einstieg ist das Übersichtsblatt: eine Zeile je Konzern und je
 * Gesellschaft, eine Spalte je Gruppe, in der Zelle die Anzahl der Fälle und
 * das Zeichen des ungünstigsten. Die Fälle selbst stehen auf der Zielseite;
 * der Klick öffnet sie mit der Gesellschaft und dem Zeitraum gewählt (R1521).
 */

/** Die acht Spalten, in ihrer Reihenfolge (R1520). */
export const GRUPPEN = [
  'Lieferung',
  'Spiegel',
  'Konzernintern',
  'KapitalUndBeteiligungen',
  'Kapitalflussrechnung',
  'Kurse',
  'Anhang',
  'Konsolidierungsprozess',
] as const

export type Pruefgruppe = (typeof GRUPPEN)[number]
export type Ueberwachungsstufe = 'aus' | 'hinweis' | 'warnung' | 'sperrt'

export type Uebersichtspruefung = { pruefung: string; anzahl: number; stufe: Ueberwachungsstufe }
export type Uebersichtszelle = {
  gruppe: Pruefgruppe
  anzahl: number
  stufe: Ueberwachungsstufe
  pruefungen: Uebersichtspruefung[]
}
export type Uebersichtszeile = {
  kreis: string | null
  ledger: string | null
  einheit: string | null
  anzahl: number
  sperrt: boolean
  zellen: Uebersichtszelle[]
  namen?: Beschriftung | null
}
export type Uebersichtsblatt = {
  von: number
  bis: number
  geprueftBis: number
  konzerne: Uebersichtszeile[]
  gesellschaften: Uebersichtszeile[]
  ohneBefund: number
  vermerke: Textbaustein[]
}
export type Ueberwachungselement = { element: string; ledger: string }
export type Ueberwachungsfestlegung = { von: number; bis: number; bestandteile: Ueberwachungselement[] }
export type Ueberwachungsstand = {
  festlegung: Ueberwachungsfestlegung | null
  uebersicht: { blaetter: Uebersichtsblatt[] } | null
}

/** Wohin der Sprung aus dem Blatt führt, und mit welcher Auswahl über den Berichten (R1521). */
export type Blattsprung = {
  ziel: Sprungziel
  einheit: string | null
  kreis: string | null
  ledger: string | null
  von: number
  bis: number
}

/** Wie viele Fälle das Blatt zählt — der Anhänger am Symbol der Arbeitsliste (R1519). Ohne Festlegung eine Aufgabe: festlegen (R1514). */
export function offeneFaelle(stand: Ueberwachungsstand | null): number {
  if (!stand) {
    return 0
  }

  if (!stand.festlegung) {
    return 1
  }

  return (stand.uebersicht?.blaetter ?? []).reduce(
    (summe, b) => summe + [...b.konzerne, ...b.gesellschaften].reduce((s, z) => s + z.anzahl, 0),
    0,
  )
}

/** Wie das Zeichen einer Zelle heißt — die Stufe der Freigabe als Zustand (R1531). */
function zustandVon(stufe: Ueberwachungsstufe): Zustandsart {
  return stufe === 'sperrt' ? 'blockiert' : 'offen'
}

/** Der Code der Prüfung, wie die Verweise ihn kennen — ohne den Gegenstand der konzerninternen Differenz. */
function grundcode(pruefung: string): string {
  return pruefung.split('/')[0] ?? pruefung
}

/** Wohin eine Prüfung führt — die Zeilen der Überwachung, die keine Prüfung der Freigabe sind, eigens (R1528). */
export function zielDerPruefung(pruefung: string): Sprungziel {
  switch (pruefung) {
    case 'monat-nicht-konsolidiert':
      return { bereich: 'abschluss', unterpunkt: 'konzernabschluesse' }
    case 'historischer-kurs-fehlt':
      return { bereich: 'stammdaten', unterpunkt: 'historische-kurse' }
    case 'inaktiv-im-kreis':
      return { bereich: 'konzernstruktur', unterpunkt: 'kreise' }
    case 'konsolidierungshinweis':
      return { bereich: 'abschluss', unterpunkt: 'konzernabschluesse' }
    default:
      return verweisJeBefund(grundcode(pruefung), {})
  }
}

function zeilenname(z: Uebersichtszeile, ledgernamen: Record<string, string>): string {
  // Ohne eigenen Namen der des Namensgebers, nie der Code (R1660).
  const name = beschriftung(z.namen ?? undefined) || (z.einheit ? gesellschaftsname(z.einheit) : z.kreis ? kreisname(z.kreis) : '')
  return z.einheit ? name : `${name} ${ledgernamen[z.ledger ?? ''] ?? ''}`.trim()
}

/** Die gewählte Zelle — verlinkt ist nur die Zahl (Entscheidung vom 2026-10-11). */
type Wahl = { zeile: Uebersichtszeile; gruppe: Pruefgruppe }

export function Ueberwachungsblatt({
  stand,
  auswahl,
  aufSprung,
  aufGeaendert,
}: {
  stand: Ueberwachungsstand | null
  auswahl: Auswahl | null
  aufSprung: (sprung: Blattsprung) => void
  aufGeaendert: (stand: Ueberwachungsstand) => void
}) {
  const [festlegen, setFestlegen] = useState(false)

  const elementname = (schluessel: string) =>
    beschriftung(auswahl?.elemente.find((e) => e.schluessel === schluessel)?.namen) || schluessel
  const ledgernamen = Object.fromEntries(
    (auswahl?.ledger ?? []).map((l) => [l.schluessel, bewertungsebenenname(l.schluessel)]),
  )

  if (!stand) {
    return null
  }

  if (festlegen || !stand.festlegung) {
    return (
      <>
        {/* Ohne Festlegung nur die Aufforderung — die Software rät nichts (R1514). */}
        {!stand.festlegung && !festlegen && (
          <p className="arbeitszeile offen">
            <Zustandssymbol art="offen" />
            <span className="text">
              {t('ueberwachung.aufforderung')}
              <span className="weg">
                <Verweissatz text={t('ueberwachung.aufforderung.weg', undefined, true)} aufVerweis={() => setFestlegen(true)} />
              </span>
            </span>
          </p>
        )}
        {festlegen && (
          <Ueberwachungsfestlegen
            auswahl={auswahl}
            festlegung={stand.festlegung}
            aufGespeichert={(neu) => {
              setFestlegen(false)
              aufGeaendert(neu)
            }}
            aufAbbrechen={() => setFestlegen(false)}
          />
        )}
      </>
    )
  }

  const f = stand.festlegung
  return (
    <>
      {/*
        **Der Kopf nennt, was überwacht wird und für welchen Zeitraum** — die
        Auflage dafür, dass die Kopfzeile nicht mehr wirkt (R1512): Wer
        vergessen hat umzuschwenken, sieht es hier.
      */}
      <p className="erlaeuterung">
        {t('ueberwachung.kopf', {
          bestandteile: f.bestandteile
            .map((b) => `${elementname(b.element)} ${ledgernamen[b.ledger] ?? ''}`.trim())
            .join(', '),
          von: periodenname(f.von),
          bis: periodenname(f.bis),
        })}{' '}
        <button type="button" className="schmal" onClick={() => setFestlegen(true)}>
          {t('ueberwachung.festlegen')}
        </button>
      </p>

      {(stand.uebersicht?.blaetter ?? []).length === 0 && <p className="hinweis">{t('ueberwachung.nochnichts')}</p>}

      {(stand.uebersicht?.blaetter ?? []).map((blatt) => (
        <Blatt
          key={`${blatt.von}-${blatt.bis}`}
          blatt={blatt}
          ledgernamen={ledgernamen}
          standardledger={f.bestandteile[0]?.ledger ?? null}
          aufSprung={aufSprung}
        />
      ))}
    </>
  )
}

function Blatt({
  blatt,
  ledgernamen,
  standardledger,
  aufSprung,
}: {
  blatt: Uebersichtsblatt
  ledgernamen: Record<string, string>
  standardledger: string | null
  aufSprung: (sprung: Blattsprung) => void
}) {
  const [wahl, setWahl] = useState<Wahl | null>(null)
  const gesellschaften = blatt.gesellschaften.length
  // Die Spaltenbreiten zieht der Anwender, wie in jeder Tabelle (7.3 Abschnitt 10, R415, R833).
  const { masse: spaltenmasse, rahmen: spaltenrahmen } = useSpaltenmasse<HTMLTableElement>('arbeitsliste:uebersicht')

  const zeilen = (titel: string, liste: Uebersichtszeile[]) =>
    liste.length > 0 && (
      <Fragment key={titel}>
        {/* Zwischenüberschriften trennen Konzerne und Gesellschaften (R1519). */}
        <tr className="zwischenkopf">
          <th colSpan={GRUPPEN.length + 1}>{titel}</th>
        </tr>
        {liste.map((z) => (
          <tr key={`${z.kreis ?? ''}-${z.ledger ?? ''}-${z.einheit ?? ''}`}>
            {/* Der Name ist Text; verlinkt ist nur die Zahl (Entscheidung vom 2026-10-11). */}
            <th>{zeilenname(z, ledgernamen)}</th>
            {GRUPPEN.map((g) => {
              const zelle = z.zellen.find((c) => c.gruppe === g)
              return (
                <td key={g} className="zahl">
                  {/* Eine leere Zelle heißt: ohne Befund — eine Null steht nicht da (R1519). */}
                  {zelle && (
                    <button type="button" className="ortsverweis" onClick={() => setWahl({ zeile: z, gruppe: g })}>
                      <Zustandssymbol art={zustandVon(zelle.stufe)} /> {anzahlwort(zelle.anzahl)}
                    </button>
                  )}
                </td>
              )
            })}
          </tr>
        ))}
      </Fragment>
    )

  // Was die gewählte Zelle trägt: je Prüfung eine Zeile der Liste.
  const getroffen = wahl
    ? (wahl.zeile.zellen.find((c) => c.gruppe === wahl.gruppe)?.pruefungen ?? []).map((p) => ({
        zeile: wahl.zeile,
        pruefung: p,
      }))
    : []

  return (
    <section className="uebersichtsblatt">
      <h2>
        {t('ueberwachung.blatt.kopf', {
          von: periodenname(blatt.von),
          bis: periodenname(blatt.bis),
          geprueft: periodenname(blatt.geprueftBis),
        })}
      </h2>
      <p className="erlaeuterung">
        {t(gesellschaften === 1 ? 'ueberwachung.blatt.ohnebefund.eins' : 'ueberwachung.blatt.ohnebefund', {
          ohne: anzahlwort(blatt.ohneBefund),
          anzahl: anzahlwort(gesellschaften),
        })}
      </p>

      <div className="tabellenrahmen">
        <table ref={spaltenrahmen} className="tabelle uebersicht ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse} />
          <thead>
            <tr>
              <th />
              {GRUPPEN.map((g) => (
                <th key={g}>{t(`ueberwachung.gruppe.${g}` as Sprachschluessel)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {zeilen(t('ueberwachung.konzerne'), blatt.konzerne)}
            {zeilen(t('ueberwachung.gesellschaften'), blatt.gesellschaften)}
          </tbody>
        </table>
      </div>

      {/* Was nicht geprüft ist und warum (R1557). */}
      {blatt.vermerke.map((v, i) => (
        <p key={i} className="hinweis">
          <Verweissatz
            text={satz(v, undefined, true)}
            aufVerweis={() =>
              aufSprung({
                ziel: { bereich: 'abschluss', unterpunkt: 'konzernabschluesse' },
                einheit: null,
                kreis: null,
                ledger: null,
                von: blatt.von,
                bis: blatt.geprueftBis,
              })
            }
          />
        </p>
      ))}

      {/*
        **Die Liste zur Wahl:** eine Zeile je Prüfung mit ihrer Anzahl; ihr
        Klick führt zur Zielseite mit der Gesellschaft und dem Zeitraum (R1521).
      */}
      {wahl && (
        <div className="uebersichtsliste">
          <h3>
            {zeilenname(wahl.zeile, ledgernamen)} · {t(`ueberwachung.gruppe.${wahl.gruppe}` as Sprachschluessel)}{' '}
            <button type="button" className="schmal" onClick={() => setWahl(null)}>
              {t('ueberwachung.liste.schliessen')}
            </button>
          </h3>
          {getroffen.length === 0 ? (
            <p className="hinweis">{t('ueberwachung.liste.leer')}</p>
          ) : (
            <ul className="vorrat">
              {getroffen.map(({ zeile, pruefung }) => (
                <li key={`${zeilenname(zeile, ledgernamen)}-${pruefung.pruefung}`} className={`arbeitszeile ${zustandVon(pruefung.stufe)}`}>
                  <Zustandssymbol art={zustandVon(pruefung.stufe)} />
                  <span className="text">
                    <button
                      type="button"
                      className="ortsverweis"
                      onClick={() =>
                        aufSprung({
                          ziel: zielDerPruefung(pruefung.pruefung),
                          einheit: zeile.einheit,
                          kreis: zeile.kreis,
                          ledger: zeile.ledger ?? standardledger,
                          von: blatt.von,
                          bis: blatt.geprueftBis,
                        })
                      }
                    >
                      {t(`pruefung.${pruefung.pruefung}` as Sprachschluessel)}
                    </button>{' '}
                    <span className="zahl">{anzahlwort(pruefung.anzahl)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}

/**
 * **Überwachung festlegen** (R1512, R1513): Bestandteile aus dem Baum der
 * Berichtsauswahl — Kreis, Ordner, Gesellschaft — je mit Bewertungsebene, und
 * ein Zeitraum von bis. Der Wert einer eigenen Dimension ist nicht wählbar.
 */
export function Ueberwachungsfestlegen({
  auswahl,
  festlegung,
  aufGespeichert,
  aufAbbrechen,
}: {
  auswahl: Auswahl | null
  festlegung: Ueberwachungsfestlegung | null
  aufGespeichert: (stand: Ueberwachungsstand) => void
  aufAbbrechen: () => void
}) {
  const ledger = auswahl?.ledger ?? []
  const elemente = (auswahl?.elemente ?? []).filter((e) => e.art !== 'dimensionswert')
  const [gewaehlt, setGewaehlt] = useState<Record<string, string>>(
    Object.fromEntries((festlegung?.bestandteile ?? []).map((b) => [b.element, b.ledger])),
  )
  const [von, setVon] = useState<number | null>(festlegung?.von ?? null)
  const [bis, setBis] = useState<number | null>(festlegung?.bis ?? null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const { masse: spaltenmasse, rahmen: spaltenrahmen } = useSpaltenmasse<HTMLTableElement>('arbeitsliste:festlegen')

  const speichern = () => {
    if (!von || !bis) {
      setMeldung(t('ueberwachung.befund.zeitraum'))
      return
    }

    api
      .ueberwachungFestlegen({
        von,
        bis,
        bestandteile: Object.entries(gewaehlt).map(([element, l]) => ({ element, ledger: l })),
      })
      .then(aufGespeichert)
      .catch((f) => setMeldung(fachlicherSatz(f)))
  }

  return (
    <section className="ueberwachungsfestlegung">
      <h2>{t('ueberwachung.festlegen')}</h2>
      <p className="erlaeuterung">{t('ueberwachung.festlegen.erlaeuterung')}</p>

      <div className="suchzeile">
        <Periodenfeld wert={von} aufWert={setVon} beschriftung={t('ueberwachung.festlegen.von')} />
        <Periodenfeld wert={bis} aufWert={setBis} beschriftung={t('ueberwachung.festlegen.bis')} />
      </div>

      <table ref={spaltenrahmen} className="tabelle ziehbaretabelle">
        <Spaltenmassleiste masse={spaltenmasse} />
        <thead>
          <tr>
            <th>{t('ueberwachung.festlegen.bestandteil')}</th>
            <th>{t('kontext.ledger')}</th>
          </tr>
        </thead>
        <tbody>
          {elemente.map((e) => {
            const l = gewaehlt[e.schluessel]
            return (
              <tr key={e.schluessel}>
                <td>
                  <label>
                    <input
                      type="checkbox"
                      checked={l !== undefined}
                      onChange={(ev) =>
                        setGewaehlt((alt) => {
                          const neu = { ...alt }
                          if (ev.target.checked) {
                            neu[e.schluessel] = ledger[0]?.schluessel ?? ''
                          } else {
                            delete neu[e.schluessel]
                          }
                          return neu
                        })
                      }
                    />{' '}
                    {beschriftung(e.namen) || e.objekt}{' '}
                    <span className="gedaempft">{t(`ueberwachung.art.${e.art}` as Sprachschluessel)}</span>
                  </label>
                </td>
                <td>
                  {l !== undefined && (
                    <select value={l} onChange={(ev) => setGewaehlt((alt) => ({ ...alt, [e.schluessel]: ev.target.value }))}>
                      {ledger.map((x) => (
                        <option key={x.schluessel} value={x.schluessel}>
                          {beschriftung(x.namen) || x.code}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {meldung && <p className="fehler">{meldung}</p>}

      <div className="aktionsreihe">
        <button type="button" className="haupt" onClick={speichern}>
          {t('ueberwachung.festlegen.speichern')}
        </button>
        {festlegung && (
          <button type="button" onClick={aufAbbrechen}>
            {t('ueberwachung.festlegen.abbrechen')}
          </button>
        )}
      </div>
    </section>
  )
}
